/**
 * URL-driven locale helpers. English lives at the existing paths; Spanish
 * lives under /es/ with identical slugs. All paths here are router paths
 * (i.e. WITHOUT the deployment BASE_URL - React Router's basename handles it).
 */
export type Locale = "en" | "es";
export const LOCALES: Locale[] = ["en", "es"];
export const DEFAULT_LOCALE: Locale = "en";
export const ES_PREFIX = "/es";

export const HTML_LANG: Record<Locale, string> = { en: "en", es: "es" };
export const OG_LOCALE: Record<Locale, string> = { en: "en_US", es: "es_US" };
export const HREFLANG: Record<Locale, string> = { en: "en-US", es: "es-US" };

/** Locale of a router pathname ("/es", "/es/", "/es/about/" -> es). */
export function getLocaleFromPath(pathname: string): Locale {
  return pathname === ES_PREFIX || pathname.startsWith(`${ES_PREFIX}/`) ? "es" : "en";
}

/** Remove the /es prefix. "/es" -> "/", "/es/about/" -> "/about/". */
export function stripLocale(pathname: string): string {
  if (getLocaleFromPath(pathname) !== "es") return pathname || "/";
  const rest = pathname.slice(ES_PREFIX.length);
  return rest === "" ? "/" : rest;
}

function isExternal(to: string): boolean {
  return /^([a-z][a-z0-9+.-]*:|\/\/|#)/i.test(to);
}

/** Paths that are files/APIs and must never be locale-prefixed. */
const NON_PAGE = /^\/(api|downloads|forms|brand|images|media|blog\/content|lead-magnets|chat|banners|silhouettes|locations\/[^/]+\.(?:png|jpe?g|webp|svg))(\/|$)|\.[a-z0-9]{2,5}($|[?#])/i;

/**
 * Localize an internal router path (may include ?query and #hash).
 * External URLs, hashes, assets and API paths are returned untouched.
 */
export function localizePath(to: string, locale: Locale): string {
  if (!to || isExternal(to) || !to.startsWith("/")) return to;
  if (NON_PAGE.test(to)) return to;
  const m = to.match(/^([^?#]*)(.*)$/)!;
  const path = stripLocale(m[1] || "/");
  if (locale === "en") return `${path}${m[2]}`;
  return `${path === "/" ? `${ES_PREFIX}/` : `${ES_PREFIX}${path}`}${m[2]}`;
}

/** Equivalent href in another locale, preserving query string and hash. */
export function switchLocalePath(
  pathname: string,
  search: string,
  hash: string,
  target: Locale,
): string {
  return localizePath(`${stripLocale(pathname)}${search}${hash}`, target);
}

/** Both language URLs for a router path (for hreflang/canonical/sitemap). */
export function alternatePaths(path: string): Record<Locale, string> {
  const base = stripLocale(path);
  return { en: localizePath(base, "en"), es: localizePath(base, "es") };
}
